// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(null);
  const [isSticky, setIsSticky] = useState(false);
  const navbarRef = useRef(null);
  const location = useLocation();

  const getMenuKey = (name) => name.toLowerCase().replace(/\s+/g, '-');

  const handleDropdownToggle = (menu) => {
    setDropdownOpen(dropdownOpen === menu ? null : menu);
  };

  const closeAllDropdowns = () => {
    setDropdownOpen(null);
    setDesktopDropdownOpen(null);
  };

  const toggleDesktopDropdown = (menu) => {
    setDesktopDropdownOpen((prev) => (prev === menu ? null : menu));
  };

  const handleDesktopDropdownKeyDown = (event, menu) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDesktopDropdown(menu);
    }
    if (event.key === 'Escape') {
      closeAllDropdowns();
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (navbarRef.current) {
        const offset = navbarRef.current.offsetTop;
        setIsSticky(window.pageYOffset > offset);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        closeAllDropdowns();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    closeAllDropdowns();
    setIsMenuOpen(false);
  }, [location.pathname, location.search]);

  const navItems = [
    { name: 'Home', path: '/' },
    {
      name: 'Membership',
      path: '/membership',
      dropdown: ['2024–2025', 'University Membership'],
      dropdownPath: '/membership/',
    },
    
    {
      name: 'Events',
      path: '/events',
      dropdown: ['2024–2025', "2025-2026"],
      dropdownPath: '/events/',
    },
    {
      name: 'Student Council',
      path: './StudentCouncil',
      dropdown: ['2024–2025'],
      dropdownPath: '/student-council/',
    },
    { name: 'Contact Us', path: '/contact-us' },
    { name: 'Gallery', path: '/gallery' },
    //{ name: 'Registration', path: 'https://docs.google.com/forms/d/e/1FAIpQLSfsT_RILNvit1lNuuuL-HiITAAm5zoqBiQ_-BCSFCvrohd-Sw/viewform?usp=header' },
  ];

  return (
    <nav
      ref={navbarRef}
      className={`bg-csi-blue text-white p-4 transition-all duration-300 z-50 text-lg ${
        isSticky ? 'fixed top-0 left-0 right-0 shadow-lg animate-slide-down' : 'relative'
      } animate-slide-up`}
    >
      <div className="container mx-auto flex justify-between items-center">
        {/* Mobile menu button */}
        <div className="md:hidden">
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-white focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
              )}
            </svg>
          </button>
        </div>

        {/* Desktop Nav Items */}
        <ul className="hidden md:flex flex-grow justify-center items-center space-x-8 w-full text-lg">
          {navItems.map((item) =>
            item.dropdown ? (
              <li
                key={item.name}
                className="relative"
                onMouseEnter={() => setDesktopDropdownOpen(item.name)}
                onMouseLeave={() => setDesktopDropdownOpen(null)}
              >
                <button
                  className="py-2 px-4 text-lg hover:bg-white hover:text-csi-blue rounded-md transition-colors duration-300 focus:outline-none flex items-center"
                  aria-haspopup="menu"
                  aria-expanded={desktopDropdownOpen === item.name}
                  onClick={() => toggleDesktopDropdown(item.name)}
                  onKeyDown={(event) => handleDesktopDropdownKeyDown(event, item.name)}
                >
                  {item.name}
                  <svg className={`ml-2 w-4 h-4 transition-transform duration-300 ${desktopDropdownOpen === item.name ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <ul className={`absolute left-0 top-full w-48 bg-white text-csi-blue rounded-md shadow-lg origin-top-left z-50 pointer-events-auto text-lg ${desktopDropdownOpen === item.name ? 'block' : 'hidden'}`}>
                  {item.dropdown.map((year) => {
                    // If this is the Events menu and the year is 2025-2026, append the open query param
                    const isEventsMenu = item.name === 'Events' && year === '2025-2026';
                    const toLink = isEventsMenu ? `${item.dropdownPath}${year}?open=cyber_jagrit_awareness` : `${item.dropdownPath}${year}`;

                    return (
                      <li key={year}>
                        {year === 'University Membership' ? (
                          <a
                            href="https://cuj.ac.in/DCSE/files/membership_csi.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeAllDropdowns}
                            className="block px-4 py-2 text-lg hover:bg-csi-blue hover:text-white transition-colors duration-300"
                          >
                            {year}
                          </a>
                        ) : (
                          <Link
                            to={toLink}
                            onClick={closeAllDropdowns}
                            className="block px-4 py-2 text-lg hover:bg-csi-blue hover:text-white transition-colors duration-300"
                          >
                            {year}
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ) : (
              <li key={item.name} className="relative group">
                <Link
                  to={item.path}
                  className="py-2 px-4 text-lg hover:bg-white hover:text-csi-blue rounded-md transition-colors duration-300"
                  onClick={closeAllDropdowns}
                >
                  {item.name}
                </Link>
              </li>
            )
          )}
        </ul>
      </div>

      {/* Mobile Nav Items */}
      <div className={`md:hidden ${isMenuOpen ? 'block' : 'hidden'} mt-4`}>
        <ul className="flex flex-col space-y-2 text-lg">
          {navItems.map((item) => (
            <li key={item.name} className="relative">
              {item.dropdown ? (
                <>
                  <button
                    onClick={() => handleDropdownToggle(getMenuKey(item.name))}
                    className="w-full text-left py-2 px-4 text-lg hover:bg-white hover:text-csi-blue rounded-md transition-colors duration-300 focus:outline-none flex justify-between items-center"
                  >
                    {item.name}
                    <svg
                      className={`ml-2 w-4 h-4 transition-transform duration-300 ${
                        dropdownOpen === getMenuKey(item.name) ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {dropdownOpen === getMenuKey(item.name) && (
                    <ul className="bg-gray-100 text-csi-blue rounded-md shadow-inner py-1 mt-2 animate-fade-in text-lg">
                      {item.dropdown.map((year) => {
                        // If this is the Events menu and the year is 2025-2026, append the open query param
                        const isEventsMenu = item.name === 'Events' && year === '2025-2026';
                        const toLink = isEventsMenu ? `${item.dropdownPath}${year}?open=cyber_jagrit_awareness` : `${item.dropdownPath}${year}`;

                        return (
                          <li key={year}>
                            {year === 'University Membership' ? (
                              <a
                                href="https://cuj.ac.in/DCSE/files/membership_csi.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  closeAllDropdowns();
                                  setIsMenuOpen(false);
                                }}
                                className="block px-4 py-2 text-lg hover:bg-csi-blue hover:text-white transition-colors duration-300"
                              >
                                {year}
                              </a>
                            ) : (
                              <Link
                                to={toLink}
                                onClick={() => {
                                  closeAllDropdowns();
                                  setIsMenuOpen(false);
                                }}
                                className="block px-4 py-2 text-lg hover:bg-csi-blue hover:text-white transition-colors duration-300"
                              >
                                {year}
                              </Link>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className="block py-2 px-4 text-lg hover:bg-white hover:text-csi-blue rounded-md transition-colors duration-300"
                  onClick={() => {
                    closeAllDropdowns();
                    setIsMenuOpen(false);
                  }}
                >
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;
